/* eslint-disable no-else-return */
/* eslint-disable react/require-default-props */
/* eslint-disable react/static-property-placement */
/* eslint-disable no-restricted-globals */
import * as React from 'react';
import ReactDOM from 'react-dom';
import classNames from 'classnames';
import { compose } from 'redux';
import _ from 'lodash';
import { withTranslation } from 'react-i18next';
import {
  format,
  addDays,
  isBefore,
  isAfter,
  isMatch,
  isSameDay,
  startOfISOWeek,
  endOfISOWeek,
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  compareAsc,
  parse,
} from 'date-fns';
import IconButtonWithBadge from 'components/Kizen/IconButtonWithBadge';
import Icon, { IconSizing } from 'components/Kizen/Icon';
import Validation from '../../Inputs/Validation';
import IntlProvider, { defaultLocale } from './IntlProvider';
import DatePicker from './DatePicker';
import {
  defaultProps,
  prefix,
  withPickerMethods,
  setTimingMargin,
  TYPE,
} from './utils';

import { getToggleWrapperClassName } from './Picker';
import QuickSelection from './QuickSelection';
import range from './range';
import {
  DateInput,
  DateInputContainer,
  DateInputSizingSpan,
  DateInputsWrapper,
  DateRangeInputContainer,
} from './DateRangeInput';

import './style.css';
import './stylemods.css';
import { requiresParsing } from './utils/parse';
import {
  calculateCalendarDate,
  calulateIsAllTime,
  findMatchingRangeIndex,
  isSameLocalDay,
  toLocalDate,
} from './utils/dateUtils';

const InvalidDate = new Date(8.65e15);

const isValidDateString = (date) => {
  return date && date.length === 10 && isMatch(date, 'MM/dd/yyyy');
};

const isValidPartialDateString = (date) => {
  return /^[\d/]*$/.test(date);
};

const isValidDate = (date) => {
  return !isNaN(new Date(date).getTime());
};

const getDateRangeErrorMessage = (start, end, t) => {
  if (start === '') {
    return t('Start date required.');
  }
  if (end === '') {
    return t('End date required.');
  }
  if (!isValidDateString(start)) {
    return t('Invalid start date.');
  }
  if (!isValidDateString(end)) {
    return t('Invalid end date.');
  }
  const d1 = new Date(start);
  const d2 = new Date(end);
  if (d2.getTime() < d1.getTime()) {
    return t('End date must come after start date.');
  }
  return null;
};

const tryCreateDate = (date) => {
  return isValidDateString(date) ? new Date(date) : InvalidDate;
};

class DateRangePicker extends React.Component {
  state = {};

  static getDerivedStateFromProps(nextProps, prevState) {
    const { value, ranges } = nextProps;

    if (typeof value === 'undefined') {
      return null;
    }

    const startValid = isValidDate(value[0]);
    const endValid = isValidDate(value[1]);

    if (
      (value[0] &&
        startValid &&
        !isSameLocalDay(value[0], prevState.value[0])) ||
      (value[1] && endValid && !isSameLocalDay(value[1], prevState.value[1]))
    ) {
      const matchingRangeIndex = findMatchingRangeIndex(
        ranges,
        value[0],
        value[1]
      );

      const nextValue = [
        startValid ? value[0] : prevState.value[0],
        endValid ? value[1] : prevState.value[1],
      ];

      return {
        value: nextValue,
        selectValue: nextValue,
        calendarDate: calculateCalendarDate(
          nextValue,
          matchingRangeIndex,
          null,
          ranges
        ),
        selectedIndex:
          matchingRangeIndex >= 0
            ? matchingRangeIndex
            : nextProps.selectedIndex,
      };
    }

    return null;
  }

  constructor(props) {
    super(props);

    const { defaultValue, value, defaultRange, ranges } = props;
    const defaultRangeIndex = ranges.findIndex((element) => {
      return element.default;
    });
    const defaultRangeValue =
      defaultRange ||
      (defaultRangeIndex !== -1 ? ranges[defaultRangeIndex].value : []);
    const activeValue = value || defaultValue || defaultRangeValue || [];

    let selectedIndex = props.selectedIndex ?? null;

    if (selectedIndex === null && !defaultRange && defaultRangeIndex !== -1) {
      selectedIndex = defaultRangeIndex;
    }

    const calendarDate = calculateCalendarDate(
      activeValue,
      selectedIndex,
      null,
      ranges
    );

    if (value) {
      const rangeMatchesValue =
        value[0] &&
        isSameLocalDay(value[0], defaultRangeValue[0]) &&
        value[1] &&
        isSameLocalDay(value[1], defaultRangeValue[1]);

      const matchingRangeIndex = findMatchingRangeIndex(
        ranges,
        value[0],
        value[1]
      );

      selectedIndex =
        matchingRangeIndex >= 0
          ? matchingRangeIndex
          : defaultRangeIndex !== -1 && rangeMatchesValue
            ? defaultRangeIndex
            : props.selectedIndex ?? null;
    }

    const parsedRangeStart = requiresParsing(activeValue[0])
      ? parse(activeValue[0], 'yyyy-MM-dd', new Date())
      : null;
    const parsedRangeEnd = requiresParsing(activeValue[1])
      ? parse(activeValue[1], 'yyyy-MM-dd', new Date())
      : null;
    const rangeStartDate =
      parsedRangeStart && !Number.isNaN(parsedRangeStart.getTime())
        ? format(parsedRangeStart, 'MM/dd/yyyy')
        : '';
    const rangeEndDate =
      parsedRangeEnd && !Number.isNaN(parsedRangeEnd.getTime())
        ? format(parsedRangeEnd, 'MM/dd/yyyy')
        : '';

    this.state = {
      value: activeValue,
      selectValue: activeValue,
      doneSelected: true,
      calendarDate,
      hoverValue: [],
      currentHoverDate: null,
      selectedIndex,
      tempSelectedIndex: null,
      showCalendar: false,
      rangeStartDate,
      rangeEndDate,
      rangeInputError: null,
    };

    this.menuContainerRef = React.createRef();
    this.wrapper = React.createRef();
    this.toggleCalendarButton = React.createRef();
    this.rangeEndDateInput = React.createRef();
    // Set to false when interacting with the calendar popup and true when
    // typing into the range inputs. This is used to know whether or not to
    // trigger the range input specific validation on blur.
    this.typingRangeInput = React.createRef(false);
  }

  componentDidMount() {
    document.addEventListener('mousedown', this.handleClickOutside);
  }

  componentWillUnmount() {
    document.removeEventListener('mousedown', this.handleClickOutside);
  }

  getCalendarDate = (value = [], nextSelectedIndex = null) => {
    const { selectedIndex } = this.state;
    const { ranges } = this.props;
    return calculateCalendarDate(
      value,
      selectedIndex,
      nextSelectedIndex,
      ranges
    );
  };

  getValue = () => {
    const { value } = this.props;
    const { value: stateValue } = this.state;

    if (typeof value !== 'undefined') {
      return value;
    }

    return stateValue || [];
  };

  getDateString() {
    const { format: formatType, ranges, t } = this.props;
    const nextValue = this.getValue();
    const { selectedIndex } = this.state;
    const startDate = nextValue && nextValue[0];
    const endDate = nextValue && nextValue[1];

    if (startDate && endDate) {
      const [start, end] = [toLocalDate(startDate), toLocalDate(endDate)].sort(
        compareAsc
      );
      let matchingRange = null;
      if (selectedIndex || selectedIndex === 0) {
        matchingRange = ranges[selectedIndex];
      } else {
        matchingRange = ranges[findMatchingRangeIndex(ranges, start, end)];
      }

      if (matchingRange && matchingRange.customPlaceholderLabel) {
        return matchingRange.localizedPlaceholderLabel(t);
      }

      return `${format(start, formatType)} - ${format(end, formatType)}`;
    }

    return '';
  }

  // hover range presets
  getWeekHoverRange = (date) => {
    const { isoWeek } = this.props;

    if (isoWeek) {
      // set to the first day of this week according to ISO 8601, 12:00 am
      return [startOfISOWeek(date), endOfISOWeek(date)];
    }

    return [startOfWeek(date), endOfWeek(date)];
  };

  getMonthHoverRange = (date) => [startOfMonth(date), endOfMonth(date)];

  getHoverRange(date) {
    const { hoverRange } = this.props;
    if (!hoverRange) {
      return [];
    }

    let hoverRangeFunc = hoverRange;
    if (hoverRange === 'week') {
      hoverRangeFunc = this.getWeekHoverRange;
    }

    if (hoverRangeFunc === 'month') {
      hoverRangeFunc = this.getMonthHoverRange;
    }

    if (typeof hoverRangeFunc !== 'function') {
      return [];
    }

    const hoverValues = hoverRangeFunc(date);
    const isHoverRangeValid =
      hoverValues instanceof Array && hoverValues.length === 2;
    if (!isHoverRangeValid) {
      return [];
    }
    if (isAfter(hoverValues[0], hoverValues[1])) {
      hoverValues.reverse();
    }
    return hoverValues;
  }

  handleClickOutside = (event) => {
    if (this.props.inputAffordance) {
      if (
        !this.toggleCalendarButton.current?.contains(event.target) &&
        !this.menuContainerRef.current?.contains(event.target)
      ) {
        if (this.state.showCalendar) {
          this.handleOK();
        }
      }
    } else {
      if (
        this.toggleCalendarButton.current &&
        this.toggleCalendarButton.current.contains(event.target)
      ) {
        if (this.state.showCalendar) {
          this.handleOK(event);
        } else {
          this.toggleCalendar();
        }
      } else if (this.menuContainerRef.current) {
        if (
          event.target &&
          !this.menuContainerRef.current.contains(event.target)
        ) {
          if (this.state.showCalendar) {
            this.handleOK(event);
          } else {
            this.toggleCalendar();
          }
        }
      }
    }
  };

  isAllTime = (nextSelectedIndex = null) => {
    const { selectedIndex } = this.state;
    const { ranges } = this.props;

    return calulateIsAllTime(selectedIndex, nextSelectedIndex, ranges);
  };

  handleChangeCalendarDate = (index, date) => {
    const { calendarDate } = this.state;
    calendarDate[index] = date;

    this.setState({ calendarDate });
  };

  handleClear = (value, closeOverlay, event) => {
    this.updateValue(event, value, closeOverlay, true);
  };

  handleQuickSelection = (value, selectedIndex) => {
    const { onOk } = this.props;
    this.typingRangeInput.current = false;
    this.setState({
      selectValue: value,
      value,
      selectedIndex,
      tempSelectedIndex: null,
      rangeInputError: null,
    });
    this.setDateRangeInput(value[0], value[1]);

    this.toggleCalendar();
    if (typeof onOk === 'function') {
      onOk(value, selectedIndex);
    }
  };

  handleOK = (event) => {
    const { onOk } = this.props;
    const { selectValue, selectedIndex } = this.state;
    this.updateValue(event);
    this.toggleCalendar();
    if (typeof onOk === 'function') {
      onOk(selectValue, selectedIndex);
    }
  };

  handleChangeSelectValue = (date, event) => {
    this.typingRangeInput.current = false;
    // do some checking somewhere in here if it matches with a quick select option?
    const { selectValue, doneSelected } = this.state;
    const { onSelect, oneTap, ranges } = this.props;
    let nextValue = [];
    let nextHoverValue = this.getHoverRange(date);

    if (doneSelected) {
      if (nextHoverValue.length) {
        nextValue = [nextHoverValue[0], nextHoverValue[1], date];
        nextHoverValue = [nextHoverValue[0], nextHoverValue[1], date];
      } else {
        nextValue = [date, undefined, date];
      }
    } else {
      if (nextHoverValue.length) {
        nextValue = [selectValue[0], selectValue[1]];
      } else {
        nextValue = [selectValue[0], date];
      }

      if (isAfter(nextValue[0], nextValue[1])) {
        nextValue.reverse();
      }

      nextValue[0] = setTimingMargin(nextValue[0]);
      nextValue[1] = setTimingMargin(nextValue[1], 'right');

      // Find if the selected values are in our ranges
      const rangeExistsIndex = findMatchingRangeIndex(
        ranges,
        nextValue[0],
        nextValue[1]
      );

      this.setState({
        selectedIndex: rangeExistsIndex >= 0 ? rangeExistsIndex : null,
        tempSelectedIndex: rangeExistsIndex,
        calendarDate: this.getCalendarDate(nextValue, rangeExistsIndex),
      });
    }

    const nextState = {
      doneSelected: !doneSelected,
      selectValue: nextValue,
      hoverValue: nextHoverValue,
      rangeInputError: null,
    };

    if (this.props.inputAffordance) {
      if (nextState.doneSelected) {
        this.setDateRangeInput(nextValue[0], nextValue[1]);
      } else {
        this.setDateRangeInput(nextValue[0]);
      }
    }

    event.persist();

    this.setState(nextState, () => {
      const { doneSelected: doneSelectedV2 } = this.state;
      // 如果是单击模式，并且是第一次点选，再触发一次点击
      if (oneTap && !doneSelectedV2) {
        this.handleChangeSelectValue(date, event);
      }
      // 如果是单击模式，并且是第二次点选，更新值，并关闭面板
      if (oneTap && doneSelectedV2) {
        this.updateValue(event);
      }

      if (typeof onSelect === 'function') onSelect(date, event);
    });
  };

  setDateRangeInput(start, end) {
    const fmt = (v) => format(v, 'MM/dd/yyyy');

    if (start && end) {
      this.setState({ rangeStartDate: fmt(start), rangeEndDate: fmt(end) });
    } else if (start) {
      this.setState({ rangeStartDate: fmt(start) });
    }
  }

  handleMouseMoveSelectValue = (date) => {
    const { doneSelected, selectValue, hoverValue, currentHoverDate } =
      this.state;
    const { hoverRange } = this.props;

    if (currentHoverDate && isSameDay(date, currentHoverDate)) {
      return;
    }

    const nextHoverValue = this.getHoverRange(date);

    if (doneSelected && !_.isUndefined(hoverRange)) {
      this.setState({
        currentHoverDate: date,
        hoverValue: nextHoverValue,
      });
      return;
    } else if (doneSelected) {
      return;
    }

    let nextValue = selectValue;

    if (!nextHoverValue.length) {
      nextValue[1] = date;
    } else if (hoverValue) {
      nextValue = [
        isBefore(nextHoverValue[0], hoverValue[0])
          ? nextHoverValue[0]
          : hoverValue[0],
        isAfter(nextHoverValue[1], hoverValue[1])
          ? nextHoverValue[1]
          : hoverValue[1],
        nextValue[2],
      ];
    }

    // If `nextValue[0]` is greater than `nextValue[1]` then reverse order
    if (isAfter(nextValue[0], nextValue[1])) {
      nextValue.reverse();
    }

    this.setState({
      currentHoverDate: date,
      selectValue: nextValue,
    });
  };

  disabledOkButton = () => {
    const { selectValue, doneSelected } = this.state;

    if (!selectValue[0] || !selectValue[1] || !doneSelected) {
      return true;
    }

    return this.disabledByBetween(
      selectValue[0],
      selectValue[1],
      TYPE.TOOLBAR_BUTTON_OK
    );
  };

  disabledShortcutButton = (value = []) => {
    if (!value[0] || !value[1]) {
      return true;
    }

    return this.disabledByBetween(value[0], value[1], TYPE.TOOLBAR_SHORTCUT);
  };

  handleDisabledDate = (date, values, type) => {
    const { disabledDate } = this.props;
    const { doneSelected } = this.state;
    if (disabledDate) {
      return disabledDate(date, values, doneSelected, type);
    }
    return false;
  };

  addPrefix = (name) => {
    const { classPrefix } = this.props;
    return prefix(classPrefix)(name);
  };

  toggleCalendar = () => {
    this.setState((ps) => ({ ...ps, showCalendar: !ps.showCalendar }));
    this.props?.broadcastState?.(this.state.showCalendar);
  };

  updateValue(event, nextSelectValue) {
    const { value, selectValue } = this.state;
    const { onChange, ranges } = this.props;

    let nextValue = !_.isUndefined(nextSelectValue)
      ? nextSelectValue
      : selectValue;

    // Odd case where you can only select 1 value and right click out of selection to hit apply
    if (nextValue && nextValue[1] === undefined) {
      // eslint-disable-next-line prefer-destructuring
      nextValue[1] = nextValue[0];
    }

    // Remove any extra dates caused by not selecting
    if (nextValue.length > 2) {
      nextValue = nextValue.slice(0, 2);
    }

    if (isAfter(nextValue[0], nextValue[1])) {
      nextValue.reverse();
    }

    setTimingMargin(nextValue[1], 'right');

    // Find if the selected values are in our ranges
    const rangeExistsIndex = findMatchingRangeIndex(
      ranges,
      nextValue[0],
      nextValue[1]
    );

    const state = {
      selectValue: nextValue || [],
      value: nextValue,
      tempSelectedIndex: null,
    };

    if (rangeExistsIndex !== -1) {
      state.selectedIndex = rangeExistsIndex;
    }

    this.setState(state);

    if (
      onChange &&
      (!isSameLocalDay(nextValue[0], value[0]) ||
        !isSameLocalDay(nextValue[1], value[1]))
    ) {
      onChange(nextValue, event);
    }
  }

  disabledByBetween(start, end, type) {
    const { disabledDate } = this.props;
    const { selectValue, doneSelected } = this.state;
    const selectStartDate = selectValue[0];
    const selectEndDate = selectValue[1];
    const nextSelectValue = [selectStartDate, selectEndDate];

    // If the date is between the start and the end
    // the button is disabled
    while (isBefore(start, end) || isSameDay(start, end)) {
      if (
        disabledDate &&
        disabledDate(start, nextSelectValue, doneSelected, type)
      ) {
        return true;
      }
      start = addDays(start, 1);
    }

    return false;
  }

  renderDropdownMenu() {
    const {
      menuClassName,
      ranges,
      isoWeek,
      limitEndYear,
      showWeekNumbers,
      showClearButton,
      placement,
      onClear,
      alwaysOpen,
      t,
      todayDate,
    } = this.props;
    const {
      calendarDate,
      selectValue,
      hoverValue,
      doneSelected,
      selectedIndex,
      tempSelectedIndex,
    } = this.state;
    const classes = classNames(
      'rs-picker-menu',
      this.addPrefix('daterange-menu'),
      menuClassName
    );

    let styleProp = {
      top: placement.top,
      left: placement.left,
      ...this.props.menuStyle,
    };

    if (this?.wrapper?.current) {
      const wrapperRect = this.wrapper.current.getBoundingClientRect();
      const left = wrapperRect.left;
      const right = wrapperRect.right;
      const top =
        wrapperRect.top +
        wrapperRect.height +
        document.documentElement.scrollTop;

      if (this.props.direction) {
        if (this.props.direction === 'right') {
          styleProp = {
            ...this.props.menuStyle,
            top,
            left,
          };
        } else {
          styleProp = {
            ...this.props.menuStyle,
            top,
            right: `calc(100% - ${right}px)`,
          };
        }
      }
    }

    const pickerProps = {
      isoWeek,
      doneSelected,
      hoverValue,
      calendarDate,
      limitEndYear,
      showWeekNumbers,
      value: selectValue,
      disabledDate: this.handleDisabledDate,
      onSelect: this.handleChangeSelectValue,
      onMouseMove: this.handleMouseMoveSelectValue,
      onChangeCalendarDate: this.handleChangeCalendarDate,
      todayDate,
    };

    return (
      <div className={classes} style={styleProp} ref={this.menuContainerRef}>
        <div className={this.addPrefix('daterange-panel')}>
          <div
            className={classNames(
              this.addPrefix('daterange-content'),
              'kizen-daterange-content'
            )}
          >
            <div className="kizen-quick-selection-container">
              <div className="kizen-quick-selection-header">
                {t('Quick Selection')}
              </div>
              <QuickSelection
                ranges={ranges}
                selectValue={selectValue}
                disabledShortcutButton={this.disabledShortcutButton}
                onShortcut={this.handleQuickSelection}
                selectedIndex={selectedIndex}
                tempSelectedIndex={tempSelectedIndex}
              />
            </div>
            <div className={this.addPrefix('daterange-calendar-group')}>
              <DatePicker index={0} {...pickerProps} />
              <DatePicker index={1} {...pickerProps} />
            </div>
          </div>
          <div className="kizen-daterangepicker-toolbar">
            {showClearButton && (
              <button
                className="clearButton"
                type="button"
                onClick={() => {
                  if (!alwaysOpen) {
                    this.toggleCalendar();
                  }
                  if (typeof onClear === 'function') {
                    onClear();
                  }
                }}
              >
                {t('Clear & Close')}
              </button>
            )}
            <button
              className="applyButton"
              type="button"
              onClick={this.handleOK}
            >
              {t('Apply')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  renderDropdownMenuInPortal() {
    return ReactDOM.createPortal(
      this.renderDropdownMenu(),
      this.props.popover || this.props.direction
        ? document.body
        : this.wrapper.current
    );
  }

  /**
   * hasError, onChange, width props are only used with this input affordance
   * onChange is called on blur for each input with [Date | string, Date | string],
   * either a valid Date instance or the invalid date string. The calendar popup state
   * is never in an invalid state, but the input can be edited
   */
  renderInputAffordance() {
    const { hasError, onChange, t, width } = this.props;
    const { rangeStartDate, rangeEndDate, rangeInputError } = this.state;
    const showLocalValidation = !hasError && Boolean(rangeInputError);

    return (
      <>
        <DateRangeInputContainer
          ref={this.toggleCalendarButton}
          onClick={() => this.setState({ showCalendar: true })}
          width={width}
          hasError={hasError || Boolean(rangeInputError)}
        >
          <DateInputsWrapper>
            <DateInputContainer enableDynamicWidth={Boolean(rangeStartDate)}>
              <DateInput
                type="text"
                placeholder="MM/DD/YYYY"
                value={rangeStartDate}
                onKeyUp={(ev) => {
                  this.typingRangeInput.current = true;
                  if (ev.key === 'Enter') {
                    this.rangeEndDateInput.current.focus();
                  }
                }}
                onBlur={() => {
                  this.rangeEndDateInput.current.focus();
                  if (typeof onChange === 'function') {
                    onChange([
                      tryCreateDate(rangeStartDate),
                      tryCreateDate(rangeEndDate),
                    ]);
                  }
                }}
                onChange={(ev) => {
                  this.setState({ rangeInputError: null });

                  if (isValidPartialDateString(ev.target.value)) {
                    this.setState({ rangeStartDate: ev.target.value });
                  }

                  if (isValidDateString(ev.target.value)) {
                    this.setState({
                      doneSelected: true,
                      selectValue: [
                        new Date(ev.target.value),
                        ...this.state.selectValue.slice(1),
                      ],
                    });
                  }
                }}
              />
              <DateInputSizingSpan>{rangeStartDate}</DateInputSizingSpan>
            </DateInputContainer>
            <code>&#8212;</code>
            <DateInputContainer>
              <DateInput
                ref={this.rangeEndDateInput}
                type="text"
                placeholder="MM/DD/YYYY"
                value={rangeEndDate}
                onKeyUp={(ev) => {
                  this.typingRangeInput.current = true;
                  if (ev.key === 'Enter') {
                    this.rangeEndDateInput.current.blur();
                    this.setState({ showCalendar: false });
                  }
                }}
                onBlur={() => {
                  if (!this.typingRangeInput.current) {
                    return;
                  }
                  if (typeof onChange === 'function') {
                    onChange([
                      tryCreateDate(rangeStartDate),
                      tryCreateDate(rangeEndDate),
                    ]);
                  }
                  const error = getDateRangeErrorMessage(
                    rangeStartDate,
                    rangeEndDate,
                    t
                  );
                  if (error) {
                    this.setState({ rangeInputError: error });
                    setTimeout(
                      () => this.setState({ rangeInputError: null }),
                      3000
                    );
                  }
                }}
                onChange={(ev) => {
                  this.setState({ rangeInputError: null });

                  if (isValidPartialDateString(ev.target.value)) {
                    this.setState({ rangeEndDate: ev.target.value });
                  }

                  if (isValidDateString(ev.target.value)) {
                    this.setState({
                      doneSelected: true,
                      selectValue: [
                        this.state.selectValue[0],
                        new Date(ev.target.value),
                      ],
                    });
                  }
                }}
              />
            </DateInputContainer>
          </DateInputsWrapper>
          <IconSizing size="12px">
            <Icon icon="calendar" color="var(--color-background-dark)" />
          </IconSizing>
        </DateRangeInputContainer>
        <Validation
          showMessage={showLocalValidation}
          message={rangeInputError}
          target={this.toggleCalendarButton.current}
        />
      </>
    );
  }

  render() {
    const {
      locale,
      style,
      title,
      alwaysOpen,
      menuOnly,
      inPortal,
      canEdit = true,
      inputAffordance,
    } = this.props;
    const { showCalendar } = this.state;
    const value = this.getValue();
    const hasValue = value && value.length > 1;
    const classes = getToggleWrapperClassName(
      'daterange',
      this.addPrefix,
      this.props,
      hasValue
    );

    return (
      <IntlProvider locale={locale}>
        <div
          className={classes}
          style={{
            ...style,
            position: this.props.inModal ? 'unset' : style?.position,
          }}
          ref={this.wrapper}
        >
          {menuOnly ? null : inputAffordance ? (
            this.renderInputAffordance()
          ) : (
            <IconButtonWithBadge
              disabled={!canEdit}
              icon="calendar"
              title={title}
              popperConfig={{
                modifiers: {
                  offset: { offset: '0, 5px' }, // Touch top of text
                  preventOverflow: { enabled: false },
                },
              }}
              badge={this.getDateString()}
              ref={this.toggleCalendarButton}
              badgeMaxWidth={!this.props.collapseBadge}
              emptyBadge={this.props.collapseBadge}
            />
          )}
          {showCalendar || alwaysOpen
            ? inPortal
              ? this.renderDropdownMenuInPortal()
              : this.renderDropdownMenu()
            : null}
        </div>
      </IntlProvider>
    );
  }
}

DateRangePicker.defaultProps = {
  limitEndYear: 1000,
  format: 'MM/dd/yy',
  locale: defaultLocale,
  ranges: [
    range.today,
    range.yesterday,
    range.thisMonth,
    range.last7Days,
    range.last30Days,
    range.thisYear,
    range.allTime,
  ],
  placement: {
    top: '100%',
    left: 0,
  },
  inModal: false,
  inPortal: true,
  alwaysOpen: false,
  menuOnly: false,
  inputAffordance: false,
  showClearButton: false,
};

const enhance = compose(
  defaultProps({
    classPrefix: 'picker',
  }),
  withPickerMethods()
);

export default withTranslation()(enhance(DateRangePicker));
